import React, { useCallback, useState } from 'react';

import { useAppContext } from '@r1-frontend/entities/Layout/App/AppContext';
import { CitySelect } from '@r1-frontend/entities/Layout/Header/commonComponents/CitySelect';
import { ConfirmCitySelection } from '@r1-frontend/entities/Layout/Header/commonComponents/ConfirmCitySelection';
import PhoneInfo from '@r1-frontend/entities/Layout/Header/commonComponents/PhoneInfo';
import CardMenu from '@r1-frontend/entities/Layout/Header/mobile/components/CardMenu';
import { Logo } from '@r1-frontend/entities/Layout/Header/mobile/components/Logo';
import TopMenu from '@r1-frontend/entities/Layout/Header/mobile/components/TopMenu';
import TopMenuWithIcons from '@r1-frontend/entities/Layout/Header/mobile/components/TopMenuWithIcons';
import { MobileHeaderContext } from '@r1-frontend/entities/Layout/Header/mobile/context';
import * as ST from '@r1-frontend/entities/Layout/Header/mobile/styled';
import { ICommonHeaderBaseProps } from '@r1-frontend/entities/Layout/Header/types';
import { useLeadGeneration } from '@r1-frontend/entities/Layout/Header/UnauthHeader/useLeadGeneration';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { Close, Menu } from '@r1-frontend/ui-react/components/svg/main';
import { EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import CallRequestByTheme from './CallRequestByTheme';

export interface ICommonActions {
    Actions: JSX.Element,
    ActionsInBurger?: (props: { closeHandler: () => void }) => JSX.Element,
}

export interface ICommonMobileHeaderProps extends ICommonHeaderBaseProps {
    isAuth: boolean,
    CommonActions: ICommonActions,
}

const CommonMobileHeader = ({
    items,
    isAuth,
    CommonActions: {
        Actions,
        ActionsInBurger,
    },
}: ICommonMobileHeaderProps): JSX.Element => {
    const { partners, service, main } = items;

    const { withShadowInMobileHeader, isScrollingUp } = useAppContext();

    // атрибут data-visible для автотестирования
    const isMobile = useMatchMediaByWidth(wideBreakpoints.laptop, false);

    const { showPhone } = useLeadGeneration();

    const [isBurgerOpen, setIsBurgerOpen] = useState(false);
    const [isCallbackOpen, setIsCallbackOpen] = useState(false);
    const [isMenuScrolling, setIsMenuScrolling] = useState(false);

    const onMenuScroll = useCallback(() => {
        setIsMenuScrolling(true);
        setTimeout(() => setIsMenuScrolling(false), 0);
    }, []);

    const toggleBurgerHandler = useCallback(() => {
        setIsBurgerOpen(!isBurgerOpen);
    }, [isBurgerOpen]);

    const closeBurgerHandler = useCallback(() => {
        setIsBurgerOpen(false);
    }, []);

    const menuItemClick = useCallback((item, menuType = 'Main menu') => {
        closeBurgerHandler();
        dataLayerPush({
            event: EVENTS.UAevent,
            category: `${menuType} click`,
            action: item,
            label: 'menu_new',
        });
    }, [closeBurgerHandler]);

    return (<>
            <ST.GlobalStyle isBlockedBody={isBurgerOpen}/>

            <MobileHeaderContext.Provider value={{ isMenuScrolling }}>
                <ST.MobileHeaderWrapper
                    withShadow={!isAuth || withShadowInMobileHeader}
                    data-test='main-menu-mobile'
                    data-visible={isMobile ? 'displayed' : 'none'}
                >
                    <ST.SmallMobileWrapper>
                        <ST.MobileTopWrapperOffset isScrollingUp={!!isScrollingUp} data-test={'s-mobile-top-offset'}>
                            <ST.MobileTopWrapper>
                                {isAuth ? null : <CitySelect/>}
                                {showPhone && <PhoneInfo/>}
                            </ST.MobileTopWrapper>
                        </ST.MobileTopWrapperOffset>

                        <ST.MobileMainWrapperOffset data-test={'s-mobile-main-offset'}>
                            <div>
                                <Logo/>
                                <ST.Right>

                                    {Actions}

                                    {!isBurgerOpen &&
                                        <ST.BurgerIcon onClick={toggleBurgerHandler}>
                                            <Menu size={26}/>
                                        </ST.BurgerIcon>
                                    }
                                </ST.Right>
                            </div>
                        </ST.MobileMainWrapperOffset>
                    </ST.SmallMobileWrapper>

                    <ST.MobileWrapper>
                        <ST.MobileMainWrapperOffset data-test={'mobile-main-offset'}>
                            <div>
                                <Logo/>
                                {isAuth ? null : <CitySelect/>}

                                <ST.Right>
                                    <PhoneInfo/>

                                    {Actions}

                                    {!isBurgerOpen &&
                                        <ST.BurgerIcon onClick={toggleBurgerHandler}>
                                            <Menu size={26}/>
                                        </ST.BurgerIcon>
                                    }
                                </ST.Right>

                                {isAuth ? null : <ConfirmCitySelection/>}
                            </div>
                        </ST.MobileMainWrapperOffset>
                    </ST.MobileWrapper>

                </ST.MobileHeaderWrapper>

                {isBurgerOpen &&
                    <ST.BurgerMenu onScroll={onMenuScroll} data-test={'mobile-burger'}>
                        <ST.BurgerMenuWrapper>
                            <ST.BurgerBlock>
                                <ST.BurgerWrapper>

                                    {isAuth ? null : <CitySelect/>}

                                    <ST.BurgerIcon onClick={toggleBurgerHandler} isBurgerOpen={true}>
                                        <Close size={20}/>
                                    </ST.BurgerIcon>
                                </ST.BurgerWrapper>
                            </ST.BurgerBlock>

                                {ActionsInBurger && <ActionsInBurger closeHandler={closeBurgerHandler}/>}

                            <ST.DropDownContainerTop>
                                <TopMenu
                                    menuItems={service}
                                    onClick={menuItemClick}
                                    clickHandler={closeBurgerHandler}
                                    analytics={'Right menu'}
                                />
                            </ST.DropDownContainerTop>
                                <ST.DropDownContainerBottom>
                                    <TopMenuWithIcons
                                        menuItems={partners}
                                        onClick={menuItemClick}
                                        isDrop={true}
                                        analytics={'Main menu'}
                                    />
                                </ST.DropDownContainerBottom>
                                <CardMenu
                                    menuItems={main}
                                    onClick={menuItemClick}
                                />
                            </ST.BurgerMenuWrapper>
                        </ST.BurgerMenu>
                    }
                    <CallRequestByTheme
                        isOpen={isCallbackOpen}
                        onClose={() => setIsCallbackOpen(false)}
                    />
            </MobileHeaderContext.Provider>
        </>
    );
};

export default CommonMobileHeader;
